import { getFieldsWithSuggestions } from '@/modules/templates/lib/dynamic-flow/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScreenWithFields, useDynamicForm } from '../DynamicFormContext';
import { DynamicFieldType } from './useDynamicFields';

export interface UseApplyStandardsReturn {
  applyingStandard: boolean;
  appliedStandard: boolean;
  applyStandardsRecursively: (currentScreen: ScreenWithFields, maxIterations?: number) => void;
  applyDynamicStandardsRecursively: (fields: DynamicFieldType[], maxIterations?: number) => void;
}

export function useApplyStandards(
  setFieldValue: (name: string, value: unknown) => void
): UseApplyStandardsReturn {
  const { formData, enrichmentContext, currentStep, getVisibleFields } = useDynamicForm();

  const [applyingStandard, setApplyingStandard] = useState(false);
  const [appliedStandard, setAppliedStandard] = useState(false);

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Reset applied standard state when step changes
  useEffect(() => {
    setAppliedStandard(false);
    setApplyingStandard(false);
  }, [currentStep]);

  const applyStandardsRecursively = useCallback(
    (currentScreen: ScreenWithFields, maxIterations = 5) => {
      let iterations = 0;

      const applyNextBatch = () => {
        if (iterations >= maxIterations) {
          setApplyingStandard(false);
          setAppliedStandard(true);
          return;
        }

        iterations++;

        const currentFormData = formDataRef.current;
        const visibleFields = getVisibleFields(currentScreen) || [];
        const fieldsWithSuggestions = getFieldsWithSuggestions(visibleFields, enrichmentContext);

        const unfilledFields = fieldsWithSuggestions.filter(
          (f) => !currentFormData[f.name] || currentFormData[f.name] === ''
        );

        if (unfilledFields.length === 0) {
          setApplyingStandard(false);
          setAppliedStandard(true);
          return;
        }

        unfilledFields.forEach((field) => {
          setFieldValue(field.name, field.suggestedValue);
        });

        setTimeout(applyNextBatch, 150);
      };

      setApplyingStandard(true);
      applyNextBatch();
    },
    [getVisibleFields, enrichmentContext, setFieldValue]
  );

  const applyDynamicStandardsRecursively = useCallback(
    (fields: DynamicFieldType[], maxIterations = 5) => {
      let iterations = 0;

      const applyNextBatch = () => {
        if (iterations >= maxIterations) {
          setApplyingStandard(false);
          setAppliedStandard(true);
          return;
        }

        iterations++;

        const currentFormData = formDataRef.current;
        const unfilledFields = fields.filter(
          (f) => f.standardValue && (!currentFormData[f.name] || currentFormData[f.name] === '')
        );

        if (unfilledFields.length === 0) {
          setApplyingStandard(false);
          setAppliedStandard(true);
          return;
        }

        unfilledFields.forEach((field) => {
          setFieldValue(field.name, field.standardValue);
        });

        setTimeout(applyNextBatch, 150);
      };

      setApplyingStandard(true);
      applyNextBatch();
    },
    [setFieldValue]
  );

  return {
    applyingStandard,
    appliedStandard,
    applyStandardsRecursively,
    applyDynamicStandardsRecursively,
  };
}
